import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Alert,
  type ButtonProps
} from '@mui/material'
import { QrCodeScanner } from '@mui/icons-material'
import { QrReader } from 'react-qr-reader'
import type { Result } from '@zxing/library'
import useDialog from '../../hooks/useDialog'

const MAC_ADDRESS_LENGTH = 12

interface RuuviTagQrCodeButtonProps extends Omit<ButtonProps, 'children'> {
  disabled?: boolean
  onMacDetected?: (macAddress: string) => void
}

function extractMacAddressFromQr(rawValue: string): string | null {
  if (!rawValue) {
    return null
  }

  const sanitized = rawValue.replace(/[^0-9A-Fa-f]/g, '')

  if (sanitized.length !== MAC_ADDRESS_LENGTH) {
    return null
  }

  return sanitized.toLowerCase()
}

function RuuviTagQrCodeButton({
  disabled,
  onMacDetected,
  ...buttonProps
}: RuuviTagQrCodeButtonProps) {
  const { t } = useTranslation('installation')

  const { open, openDialog, closeDialog } = useDialog()

  const { variant, color, startIcon, ...restButtonProps } = buttonProps

  function handleClose() {
    closeDialog()
  }

  function handleScanResult(result: Result | null | undefined) {
    if (!result) {
      return
    }

    const parsed = extractMacAddressFromQr(result.getText())

    if (!parsed) {
      return
    }

    onMacDetected?.(parsed)
    handleClose()
  }

  return (
    <>
      <Tooltip title={t('ruuviTagQrCodeTooltip')} placement="top">
        <Box component="span" sx={{ display: 'inline-flex' }}>
          <Button
            variant={variant ?? 'outlined'}
            color={color ?? 'primary'}
            startIcon={startIcon ?? <QrCodeScanner />}
            onClick={openDialog}
            disabled={disabled}
            {...restButtonProps}
          >
            {t('scanRuuviTagQrCode')}
          </Button>
        </Box>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{t('scanRuuviTagQrCode')}</DialogTitle>
        <DialogContent>
          <Box sx={{ backgroundColor: 'black' }}>
            <Box sx={{ margin: 'auto', maxWidth: '800px' }}>
              <QrReader
                scanDelay={500}
                constraints={{ facingMode: 'environment' }}
                onResult={(result) => handleScanResult(result)}
                videoContainerStyle={{ backgroundColor: 'black' }}
              />
            </Box>
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            {t('ruuviTagQrCodeTooltip')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button variant="text" color="inherit" onClick={handleClose}>
            {t('cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default RuuviTagQrCodeButton
